/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';

import {FormFieldAttribute} from '../../../../../../models/FormCategory';
import {EChartsOption} from 'echarts';
import {GeneralDataHandlingService} from '../../../../../../services/general.data-handling.service';
import {registerTheme} from '@siemens/ix-echarts';
import * as echarts from 'echarts/core';
import {NgxEchartsDirective} from 'ngx-echarts';
import {AppSettings} from '../../../../../../models/Constants';
import {PassportDto} from '../../../../../../models/Passport';
import {TranslateService} from '@ngx-translate/core';
import {ThemeService} from '../../../../../../services/theme.service';
import {PassportTranslationService} from '../../../../../../services/passport-translation.service';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-line',
  imports: [NgxEchartsDirective],
  templateUrl: './line.component.html',
  styleUrl: './line.component.scss',
})
export class LineComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly dataHandler = inject(GeneralDataHandlingService);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly themeService = inject(ThemeService);
  private readonly passportTranslation = inject(PassportTranslationService);

  @Input({required: true}) attribute!: FormFieldAttribute;
  @Input({required: true}) passport!: PassportDto['data'];
  @Input({required: true}) schemaId!: string;

  options: EChartsOption = {};

  private changeLangSubscription?: Subscription;

  ngAfterViewInit() {
    const container = document.querySelector(
      `#line-chart-container-${this.attribute.name}`,
    ) as HTMLElement;
    if (container) {
      container.style.height = '400px';
    }
    setTimeout(() => {
      this.options = this.buildOptions();
    }, AppSettings.INPUT_TO_REQUEST_WAIT_TIME_IN_MS);
    this.cdr.detectChanges();
  }

  ngOnInit() {
    registerTheme(echarts);

    // Re-build the chart in the newly selected language once ngx-translate
    // has finished loading it. The language selection/loading itself is
    // already handled elsewhere; here we only react to the resulting event.
    this.changeLangSubscription = this.translate.onLangChange.subscribe(() => {
      this.options = this.buildOptions();
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.changeLangSubscription?.unsubscribe();
  }

  buildOptions(): EChartsOption {
    // Determine the x-axis attribute
    const xAxisAttribute: FormFieldAttribute = (this.attribute.attributes ||
      [])[0];
    const lastAttribute = (this.attribute.attributes || []).slice(-1)[0];
    const yAxisAttributes = (this.attribute.attributes as any).slice(1);
    const axisNames = {
      x: `${this.passportTranslation.translateByPath(
        xAxisAttribute.valuePath,
        this.schemaId,
        xAxisAttribute.translation.label,
        this.attribute.valuePath,
      )} (${this.getUnit(xAxisAttribute.unit || '')})`,
      y: `${this.passportTranslation.translateByPath(
        lastAttribute.valuePath,
        this.schemaId,
        lastAttribute.translation.label,
        this.attribute.valuePath,
      )} (${this.getUnit(lastAttribute.unit || '')})`,
    };

    const data = (
      this.dataHandler.getValueByPath(
        this.passport,
        this.attribute.valuePath,
      ) as any
    ).sort(
      (a: any, b: any) =>
        a[xAxisAttribute.valuePath] - b[xAxisAttribute.valuePath],
    );

    return {
      xAxis: {
        name: axisNames.x,
        type: 'category',
        data: data.map((item: any) =>
          this.dataHandler.getValueByPath(item, xAxisAttribute.valuePath),
        ),
        nameLocation: 'middle',
        nameGap: 24,
      },
      grid: {
        left: 60,
        right: 0,
        top: 20,
        bottom: 50,
      },
      yAxis: {
        name: axisNames.y,
        type: 'value',
        nameLocation: 'middle',
        nameGap: 40,
        nameRotate: 90,
      },
      series: yAxisAttributes.map((attr: FormFieldAttribute) => {
        return {
          name: this.passportTranslation.translateByPath(
            attr.valuePath,
            this.schemaId,
            attr.translation.label,
            this.attribute.valuePath,
          ),
          type: 'line',
          data: data.map((item: any) =>
            this.dataHandler.getValueByPath(item, attr.valuePath),
          ),
        };
      }),
    };
  }

  getChartTheme() {
    return this.themeService.actualChartTheme;
  }

  getUnit(unit: string): string {
    const translateValue = `general.units.${unit}`;
    const translated = this.translate.instant(translateValue);
    if (translated === translateValue) {
      return unit;
    } else {
      return translated;
    }
  }
}
